import isEmpty from 'lodash/isEmpty'
import { createSelector } from 'reselect'

import {
  THERMOCYCLER_MODULE_TYPE,
  WASTE_CHUTE_CUTOUT,
} from '@opentrons/shared-data'

import {
  getAdditionalEquipmentEntities,
  getUnsavedForm,
} from '../step-forms/selectors'
import { timelineFrameBeforeActiveItem } from '../top-selectors/timelineFrames'

import type { HintKey, HintParams } from '.'
import type { BaseState, Selector } from '../types'

const rootSelector = (state: BaseState): BaseState['tutorial'] => state.tutorial

export const getHint: Selector<HintParams | null | undefined> = createSelector(
  rootSelector,
  tutorial => {
    const dismissedKeys = Object.keys(tutorial.dismissedHints)
    const hints = tutorial.hints.filter(
      hint => !dismissedKeys.includes(hint.hintKey)
    )
    // TODO: Ian 2018-10-08 ordering of multiple hints is TBD.
    // For now, show 1 non-dismissed hint at a time
    return hints[0]
  }
)
export const getDismissedHints: Selector<HintKey[]> = createSelector(
  rootSelector,
  tutorial => {
    const dismissedKeys = Object.keys(tutorial.dismissedHints) as HintKey[]
    return dismissedKeys
  }
)
export const getCanClearHintDismissals: Selector<boolean> = createSelector(
  rootSelector,
  tutorial => !isEmpty(tutorial.dismissedHints)
)
export const shouldShowCoolingHint: Selector<boolean> = createSelector(
  timelineFrameBeforeActiveItem, // TODO(IL, 2020-12-15): this shouldn't use activeItem bc that is tied to hover state
  getUnsavedForm,
  (prevTimelineFrame, unsavedForm) => {
    if (unsavedForm?.stepType !== 'thermocycler') {
      return false
    }

    // TODO(IL, 2020-12-15): this might not be needed if we stop using activeItem. There should always be a prev frame IRL
    if (prevTimelineFrame == null) {
      return false
    }

    const { moduleId } = unsavedForm
    const prevModuleState =
      prevTimelineFrame.robotState.modules[moduleId]?.moduleState

    if (prevModuleState && prevModuleState.type === THERMOCYCLER_MODULE_TYPE) {
      const prevLidTemp = prevModuleState.lidTargetTemp
      const nextLidTemp = unsavedForm.lidTargetTemp
      if (prevLidTemp === null || nextLidTemp === null) return false
      return nextLidTemp < prevLidTemp
    } else {
      console.error('expected thermocycler module')
    }

    return false
  }
)
export const shouldShowWasteChuteHint: Selector<boolean> = createSelector(
  timelineFrameBeforeActiveItem,
  getUnsavedForm,
  getAdditionalEquipmentEntities,
  (prevTimelineFrame, unsavedForm, additionalEquipmentEntities) => {
    const hasWasteChute = Object.values(additionalEquipmentEntities).some(
      ae => ae.name === 'wasteChute'
    )
    if (unsavedForm?.stepType !== 'moveLabware' || !hasWasteChute) {
      return false
    }
    if (prevTimelineFrame == null) {
      return false
    }
    const { newLocation } = unsavedForm
    if (newLocation === WASTE_CHUTE_CUTOUT) {
      return true
    }

    return false
  }
)
